import { useEffect } from 'react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { RouterProvider } from '@belong/common';
import classNames from 'classnames/bind';
import HeaderContainer from 'components/HeaderContainer/HeaderContainer';
import MetaTitle from 'components/Metatags/MetaTitle';
import Space from 'corecomponents/Space/Space';
import { Text } from 'design-system';
import PropTypes from 'prop-types';
import qs from 'query-string';
import styles from './Page.module.css';

const cx = classNames.bind(styles);

// Since we want to use router in the same way on every package, we need to match the next/router api
function routerAdapter({ history, location, match }) {
  return {
    // Not supported in next, but for sure we are gonna need somewhere
    match,
    pathname: location.pathname,
    query: qs.parse(location.search),
    push: history.push,
    replace: history.replace,
    back: history.goBack,
  };
}
// Since we want to use Link in the same way on every package, we need to match the next/link api
function LinkAdapter({ href, children, ...rest }) {
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}
LinkAdapter.withoutAnchor = true;

/**
 * `withRouter` types its result as `ComponentClass<Omit<P, …>>`, which carries no
 * `defaultProps`, so the optionality the five defaults below give these props is
 * lost and every call site is asked for all of them. Since the props are declared
 * here they survive that wrapper, which is why they are annotated rather than
 * left to inference.
 *
 * `history`, `location` and `match` are injected by `withRouter` and taken back
 * out of the props by its `Omit`, so no call site passes them. They stay required
 * here because `withRouter` constrains its argument to `RouteComponentProps`,
 * which declares all three.
 *
 * @param {{
 *   children: import('react').ReactNode,
 *   header?: boolean,
 *   headerMainProps?: object | null,
 *   history: any,
 *   location: any,
 *   match: any,
 *   preserveScrollOnLocationChange?: boolean,
 *   preserveScrollOnlyOnMount?: boolean,
 * }} props
 */
function Page({
  children,
  header,
  headerMainProps,
  history,
  location,
  match,
  preserveScrollOnLocationChange,
  preserveScrollOnlyOnMount,
}) {
  useEffect(() => {
    if (!preserveScrollOnLocationChange) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    if (!preserveScrollOnlyOnMount && !preserveScrollOnLocationChange) {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <RouterProvider
      link={LinkAdapter}
      router={routerAdapter({ history, location, match })}
    >
      <div className={cx('page')}>
        <MetaTitle />
        {header && <HeaderContainer headerMainProps={headerMainProps} />}
        {children}
      </div>
    </RouterProvider>
  );
}

Page.propTypes = {
  children: PropTypes.node.isRequired,
  headerMainProps: PropTypes.object,
  header: PropTypes.bool,
  location: PropTypes.objectOf(PropTypes.any),
  preserveScrollOnLocationChange: PropTypes.bool,
  preserveScrollOnlyOnMount: PropTypes.bool,
};

Page.defaultProps = {
  headerMainProps: null,
  header: true,
  preserveScrollOnLocationChange: false,
  preserveScrollOnlyOnMount: false,
};

Page.propTypes = {};

/**
 * @param {{
 *   children?: import('react').ReactNode,
 *   ignoreOffset?: string[],
 * }} props
 */
Page.OffsetGrid = ({ children, ignoreOffset }) => {
  const allClassNames = {
    'offset-grid': true,
  };

  if (ignoreOffset?.length) {
    ignoreOffset.forEach((breakpoint) => {
      allClassNames[`ignore-${breakpoint}`] = true;
    });
  }

  return <div className={cx(allClassNames)}>{children}</div>;
};

/**
 * @param {{
 *   centerSectionTitle?: boolean,
 *   children?: import('react').ReactNode,
 *   firstSection?: boolean,
 *   removeBottomSpacing?: boolean,
 *   sectionPretitle?: import('react').ReactNode,
 *   sectionSubtitle?: import('react').ReactNode,
 *   sectionTitle?: import('react').ReactNode,
 *   separatorType?: string,
 * }} props
 */
Page.Section = ({
  firstSection,
  centerSectionTitle,
  sectionPretitle,
  sectionTitle,
  sectionSubtitle,
  separatorType,
  removeBottomSpacing,
  children,
}) => {
  const getSectionTitle = () => {
    if (sectionTitle && sectionSubtitle) {
      return (
        <div
          className={cx(
            'section-title-wrapper',
            { centerSectionTitle },
            { firstSection },
          )}
        >
          <div className={cx('section-title')}>
            <Text>{sectionTitle}</Text>
          </div>
          <Space value="xs" />
          <div className={cx('section-subtitle')}>
            <Text>{sectionSubtitle}</Text>
          </div>
        </div>
      );
    }
    if (sectionTitle && sectionPretitle) {
      return (
        <div className={cx('section-title-wrapper', { centerSectionTitle })}>
          <div className={cx('section-pretitle')}>
            <Text>{sectionPretitle}</Text>
          </div>
          <Space value="xs" />
          <div className={cx('section-title')}>
            <Text>{sectionTitle}</Text>
          </div>
        </div>
      );
    }
    if (sectionSubtitle) {
      return (
        <div
          className={cx(
            'section-title-wrapper',
            { centerSectionTitle },
            { firstSection },
          )}
        >
          <div className={cx('section-subtitle')}>
            <Text>{sectionSubtitle}</Text>
          </div>
        </div>
      );
    }
    if (sectionTitle) {
      return (
        <div
          className={cx(
            'section-title-wrapper',
            { centerSectionTitle },
            { firstSection },
          )}
        >
          <div className={cx('section-title')}>
            <Text>{sectionTitle}</Text>
          </div>
        </div>
      );
    }
    return null;
  };
  return (
    <div
      className={cx('section-wrapper', { removeBottomSpacing }, separatorType)}
    >
      {getSectionTitle()}
      <div className={cx('section')}>{children}</div>
    </div>
  );
};

const RoutedPage = withRouter(Page);

/**
 * `withRouter` types its result as `ComponentClass<Omit<P, …>> &
 * WithRouterStatics<C>`, and `WithRouterStatics` carries only `WrappedComponent`
 * — so `OffsetGrid` and `Section` are erased from the type even though
 * hoist-non-react-statics keeps them at runtime. Re-declaring them here is what
 * lets `<Page.OffsetGrid>` type-check at its six call sites; nothing changes at
 * runtime.
 */
export default /** @type {typeof RoutedPage & Pick<typeof Page, 'OffsetGrid' | 'Section'>} */ (
  RoutedPage
);
